import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Repeat } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatGeneratedAt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const shortenGroupId = (id) => {
  if (!id) return '—';
  // Show first 8 chars — enough for staff to spot two sessions in the same
  // group without exposing the full UUID.
  return `${String(id).slice(0, 8)}…`;
};

/**
 * Read-only "Repeat Information" section shown near the bottom of the
 * Edit Session dialog when the session was generated via Phase 85.3
 * Repeat Sessions. Purely informational — no edit / delete series
 * controls in this phase.
 */
export default function RepeatInformationCard({ recurrenceRules }) {
  const [generatedByName, setGeneratedByName] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const uid = recurrenceRules?.generated_by;
    if (!uid) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/${uid}`);
        if (!cancelled) {
          setGeneratedByName(data?.full_name || data?.name || data?.email || uid);
        }
      } catch (e) {
        if (!cancelled) setGeneratedByName(uid);
      }
    })();
    return () => { cancelled = true; };
  }, [recurrenceRules?.generated_by]);

  if (!recurrenceRules?.recurrence_group_id) return null;

  return (
    <div
      className="mt-4 border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-800/50"
      data-testid="repeat-information-card"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Repeat className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Repeat Information</p>
      </div>
      <ul className="text-[11px] text-gray-600 dark:text-gray-400 space-y-1">
        <li>• Created using Repeat Sessions</li>
        <li>
          • Recurrence Group:{' '}
          <span
            className="font-mono text-gray-700 dark:text-gray-300"
            title={recurrenceRules.recurrence_group_id}
            data-testid="repeat-info-group-id"
          >
            {shortenGroupId(recurrenceRules.recurrence_group_id)}
          </span>
        </li>
        <li>
          • Generated on:{' '}
          <span className="text-gray-700 dark:text-gray-300" data-testid="repeat-info-generated-at">
            {formatGeneratedAt(recurrenceRules.generated_at)}
          </span>
        </li>
        <li>
          • Generated by:{' '}
          <span className="text-gray-700 dark:text-gray-300" data-testid="repeat-info-generated-by">
            {generatedByName || (recurrenceRules.generated_by ? 'Loading…' : '—')}
          </span>
        </li>
      </ul>
    </div>
  );
}
